import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogIn, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";

export default function AdminLogin() {
  const { user, login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid place-items-center px-6 mesh-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="rounded-3xl glass border-white/[0.08] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-9 w-9 grid place-items-center rounded-lg bg-nexus-cyan/10 border border-nexus-cyan/30">
              <Lock className="h-4 w-4 text-nexus-cyan" />
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan">Admin</p>
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Sign in to the studio.</h1>
          <p className="mt-2 text-sm text-zinc-400">Manage blog posts and review contact submissions.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 inline-block">Email</span>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm outline-none focus:border-nexus-cyan/60"
                placeholder="admin@fullstackendeavours.in"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 inline-block">Password</span>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm outline-none focus:border-nexus-cyan/60"
                placeholder="••••••••"
              />
            </label>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-6 py-3 text-sm hover:brightness-110 transition disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <LogIn className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
